import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, eur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ImageUpload } from "@/components/ImageUpload";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", description: "", price: "", flower: "", crystal: "", stock: 8, featured: false, images: "", collection_slug: "", subgroup: "" };
const C_EMPTY = { name: "", description: "", cover_image: "", gallery: [], order: 0 };
const ORDER_STATUSES = ["confermato", "in preparazione", "spedito", "consegnato", "annullato"];
const CARRIERS = [
  { id: "poste", name: "Poste Italiane" },
  { id: "brt", name: "BRT" },
  { id: "gls", name: "GLS" },
  { id: "sda", name: "SDA" },
  { id: "dhl", name: "DHL" },
  { id: "ups", name: "UPS" },
  { id: "gerico", name: "Altro corriere" },
];

export default function Admin() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [collections, setCollections] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [cOpen, setCOpen] = useState(false);
  const [cEditing, setCEditing] = useState(null);
  const [cForm, setCForm] = useState(C_EMPTY);
  const [trackOrder, setTrackOrder] = useState(null);
  const [trackInput, setTrackInput] = useState("");
  const [trackCarrier, setTrackCarrier] = useState("poste");

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/orders/all").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/newsletter/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
    api.get("/collections").then((r) => setCollections(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const cSet = (k) => (e) => setCForm({ ...cForm, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p, price: String(p.price), images: (p.images || []).join(", ") });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, description: form.description, price: parseFloat(form.price) || 0,
      flower: form.flower, crystal: form.crystal, stock: parseInt(form.stock) || 0,
      featured: !!form.featured, category: "composizioni",
      collection_slug: form.collection_slug || "", subgroup: form.subgroup || "",
      images: form.images.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Prodotto aggiornato" : "Prodotto creato");
      setOpen(false); load();
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const openNewColl = () => { setCEditing(null); setCForm(C_EMPTY); setCOpen(true); };
  const openEditColl = (c) => { setCEditing(c); setCForm({ ...C_EMPTY, ...c, gallery: c.gallery || [] }); setCOpen(true); };
  const addGalleryItem = () => setCForm((f) => ({ ...f, gallery: [...(f.gallery || []), { image: "", caption: "" }] }));
  const updateGalleryItem = (idx, key, val) => setCForm((f) => ({ ...f, gallery: f.gallery.map((g, i) => i === idx ? { ...g, [key]: val } : g) }));
  const removeGalleryItem = (idx) => setCForm((f) => ({ ...f, gallery: f.gallery.filter((_, i) => i !== idx) }));
  const saveColl = async (e) => {
    e.preventDefault();
    const payload = { name: cForm.name, description: cForm.description, cover_image: cForm.cover_image, gallery: (cForm.gallery || []).filter((g) => g.image), order: parseInt(cForm.order) || 0 };
    try {
      if (cEditing) await api.put(`/collections/${cEditing.id}`, payload);
      else await api.post("/collections", payload);
      toast.success(cEditing ? "Collezione aggiornata" : "Collezione creata");
      setCOpen(false); load();
    } catch { toast.error("Errore nel salvataggio"); }
  };
  const delColl = async (id) => {
    if (!window.confirm("Eliminare questa collezione? Gli articoli resteranno ma senza collezione.")) return;
    await api.delete(`/collections/${id}`);
    toast.success("Collezione eliminata"); load();
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo incanto?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Prodotto eliminato"); load();
  };

  const changeStatus = async (orderId, status) => {
    const order = orders.find((o) => o.id === orderId);
    if (status === "spedito") {
      setTrackOrder(order);
      setTrackInput(order?.tracking_code || "");
      setTrackCarrier(order?.carrier || "poste");
      return;
    }
    setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status } : o));
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      toast.success(`Stato aggiornato: ${status} · email inviata al cliente`);
    } catch { toast.error("Errore aggiornamento stato"); load(); }
  };

  const delOrder = async (orderId) => {
    if (!window.confirm("Eliminare definitivamente questo ordine? L'operazione non è reversibile.")) return;
    try {
      await api.delete(`/orders/${orderId}`);
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      toast.success("Ordine eliminato");
    } catch { toast.error("Errore durante l'eliminazione"); }
  };

  const submitTracking = async () => {
    if (!trackInput.trim()) { toast.error("Inserisci il codice di spedizione"); return; }
    const orderId = trackOrder.id;
    try {
      const { data } = await api.put(`/orders/${orderId}/status`, { status: "spedito", tracking_code: trackInput.trim(), carrier: trackCarrier });
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, ...data } : o));
      toast.success("Ordine spedito · email con tracking inviata al cliente");
      setTrackOrder(null); setTrackInput("");
    } catch { toast.error("Errore aggiornamento stato"); }
  };

  return (
    <div className="relative z-10 pt-32 pb-24 max-w-[1400px] mx-auto px-6 md:px-10">
      <h1 className="font-serif text-5xl md:text-6xl font-light mb-10" data-testid="admin-title">Pannello Incanti</h1>

      <Tabs defaultValue="products">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="products" data-testid="tab-products">Prodotti ({products.length})</TabsTrigger>
          <TabsTrigger value="collections" data-testid="tab-collections">Collezioni ({collections.length})</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Ordini ({orders.length})</TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="tab-newsletter">Newsletter ({subscribers.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-8">
          <div className="flex justify-end mb-6">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-gold hover:bg-gold/90 text-white" onClick={openNew} data-testid="new-product-btn">
                  <Plus size={16} className="mr-1" /> Nuovo incanto
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">{editing ? "Modifica" : "Nuovo"} incanto</DialogTitle>
                  <DialogDescription>Compila i dettagli della composizione botanica.</DialogDescription>
                </DialogHeader>
                <form onSubmit={save} className="space-y-4" data-testid="product-form">
                  <div><Label>Nome</Label><Input required value={form.name} onChange={set("name")} className="mt-1" data-testid="pf-name" /></div>
                  <div><Label>Descrizione</Label><Textarea required value={form.description} onChange={set("description")} className="mt-1" rows={3} data-testid="pf-desc" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Prezzo (€)</Label><Input required type="number" step="0.01" value={form.price} onChange={set("price")} className="mt-1" data-testid="pf-price" /></div>
                    <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={set("stock")} className="mt-1" data-testid="pf-stock" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Fiore</Label><Input value={form.flower} onChange={set("flower")} className="mt-1" data-testid="pf-flower" /></div>
                    <div><Label>Cristallo</Label><Input value={form.crystal} onChange={set("crystal")} className="mt-1" data-testid="pf-crystal" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Collezione</Label>
                      <Select value={form.collection_slug || "none"} onValueChange={(v) => setForm({ ...form, collection_slug: v === "none" ? "" : v })}>
                        <SelectTrigger className="mt-1" data-testid="pf-collection"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nessuna</SelectItem>
                          {collections.map((c) => (
                            <SelectItem key={c.slug} value={c.slug} data-testid={`pf-collection-${c.slug}`}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Sottogruppo (facoltativo)</Label><Input value={form.subgroup} onChange={set("subgroup")} placeholder="es. Bomboniere" className="mt-1" data-testid="pf-subgroup" /></div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label>Immagini (URL separati da virgola)</Label>
                      <ImageUpload testid="pf-upload" label="Carica foto" onUploaded={(url) => setForm((f) => ({ ...f, images: f.images ? `${f.images}, ${url}` : url }))} />
                    </div>
                    <Textarea value={form.images} onChange={set("images")} className="mt-1" rows={2} data-testid="pf-images" />
                    {form.images && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {form.images.split(",").map((u) => u.trim()).filter(Boolean).map((u, i) => (
                          <img key={i} src={u} alt="" className="h-14 w-12 object-cover rounded border border-border" />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3"><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="pf-featured" /><Label>In evidenza in home</Label></div>
                  <DialogFooter><Button type="submit" className="rounded-full bg-gold hover:bg-gold/90 text-white" data-testid="pf-save">Salva</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="border border-border rounded-lg overflow-hidden">
            <Table data-testid="products-table">
              <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Collezione</TableHead><TableHead>Prezzo</TableHead><TableHead>Stock</TableHead><TableHead>Home</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} data-testid={`product-row-${p.slug}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {collections.find((c) => c.slug === p.collection_slug)?.name || "—"}
                      {p.subgroup ? <span className="text-xs"> · {p.subgroup}</span> : ""}
                    </TableCell>
                    <TableCell>{eur(p.price)}</TableCell>
                    <TableCell>{p.stock}</TableCell>
                    <TableCell>{p.featured ? "✦" : "—"}</TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => openEdit(p)} className="p-2 hover:text-gold transition-colors" data-testid={`edit-${p.slug}`}><Pencil size={16} /></button>
                      <button onClick={() => del(p.id)} className="p-2 hover:text-destructive transition-colors" data-testid={`delete-${p.slug}`}><Trash2 size={16} /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="collections" className="mt-8">
          <div className="flex justify-end mb-6">
            <Dialog open={cOpen} onOpenChange={setCOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-gold hover:bg-gold/90 text-white" onClick={openNewColl} data-testid="new-collection-btn">
                  <Plus size={16} className="mr-1" /> Nuova collezione
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">{cEditing ? "Modifica" : "Nuova"} collezione</DialogTitle>
                  <DialogDescription>Le collezioni raggruppano i tuoi articoli nel negozio.</DialogDescription>
                </DialogHeader>
                <form onSubmit={saveColl} className="space-y-4" data-testid="collection-form">
                  <div><Label>Nome</Label><Input required value={cForm.name} onChange={cSet("name")} className="mt-1" data-testid="cf-name" /></div>
                  <div><Label>Descrizione</Label><Textarea value={cForm.description} onChange={cSet("description")} className="mt-1" rows={3} data-testid="cf-desc" /></div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label>Immagine di copertina</Label>
                      <ImageUpload testid="cf-upload" label="Carica foto" onUploaded={(url) => setCForm((f) => ({ ...f, cover_image: url }))} />
                    </div>
                    <Input value={cForm.cover_image} onChange={cSet("cover_image")} placeholder="oppure incolla un URL" className="mt-1" data-testid="cf-cover" />
                    {cForm.cover_image && <img src={cForm.cover_image} alt="" className="mt-2 h-32 w-full object-cover rounded border border-border" />}
                  </div>
                  <div className="border-t border-border pt-4">
                    <div className="flex items-center justify-between mb-1">
                      <Label>Galleria abbinamenti (senza prezzo)</Label>
                      <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={addGalleryItem} data-testid="cf-add-gallery">
                        <Plus size={14} className="mr-1" /> Aggiungi foto
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">Foto di cofanetti e bomboniere abbinati, con una descrizione. Appaiono in cima alla collezione, senza prezzo.</p>
                    <div className="space-y-4">
                      {(cForm.gallery || []).map((g, idx) => (
                        <div key={idx} className="border border-border rounded-lg p-3 space-y-2" data-testid={`cf-gallery-item-${idx}`}>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Abbinamento {idx + 1}</span>
                            <div className="flex items-center gap-2">
                              <ImageUpload testid={`cf-gallery-upload-${idx}`} label="Carica foto" onUploaded={(url) => updateGalleryItem(idx, "image", url)} />
                              <button type="button" onClick={() => removeGalleryItem(idx)} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors" data-testid={`cf-gallery-remove-${idx}`}><Trash2 size={15} /></button>
                            </div>
                          </div>
                          <Input value={g.image} onChange={(e) => updateGalleryItem(idx, "image", e.target.value)} placeholder="URL foto (o carica)" data-testid={`cf-gallery-image-${idx}`} />
                          {g.image && <img src={g.image} alt="" className="h-28 w-full object-cover rounded border border-border" />}
                          <Textarea value={g.caption} onChange={(e) => updateGalleryItem(idx, "caption", e.target.value)} rows={2} placeholder="Descrizione abbinamento (es. Cofanetto Rosa Antico + bomboniera coordinata)" data-testid={`cf-gallery-caption-${idx}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div><Label>Ordine di visualizzazione</Label><Input type="number" value={cForm.order} onChange={cSet("order")} className="mt-1" data-testid="cf-order" /></div>
                  <DialogFooter><Button type="submit" className="rounded-full bg-gold hover:bg-gold/90 text-white" data-testid="cf-save">Salva</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="border border-border rounded-lg overflow-hidden">
            <Table data-testid="collections-table">
              <TableHeader><TableRow><TableHead>Copertina</TableHead><TableHead>Nome</TableHead><TableHead>Articoli</TableHead><TableHead>Ordine</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
              <TableBody>
                {collections.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nessuna collezione ancora.</TableCell></TableRow>
                ) : collections.map((c) => (
                  <TableRow key={c.id} data-testid={`collection-row-${c.slug}`}>
                    <TableCell>{c.cover_image ? <img src={c.cover_image} alt="" className="h-12 w-10 object-cover rounded" /> : "—"}</TableCell>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{c.product_count}</TableCell>
                    <TableCell>{c.order}</TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => openEditColl(c)} className="p-2 hover:text-gold transition-colors" data-testid={`edit-coll-${c.slug}`}><Pencil size={16} /></button>
                      <button onClick={() => delColl(c.id)} className="p-2 hover:text-destructive transition-colors" data-testid={`delete-coll-${c.slug}`}><Trash2 size={16} /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-8">
          <div className="border border-border rounded-lg overflow-hidden">
            <Table data-testid="orders-table">
              <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead>Articoli</TableHead><TableHead>Totale</TableHead><TableHead>Pagamento</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
              <TableBody>
                {orders.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nessun ordine ancora.</TableCell></TableRow>
                ) : orders.map((o) => (
                  <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                    <TableCell>{o.shipping?.full_name}</TableCell>
                    <TableCell>{o.items?.reduce((s, i) => s + i.quantity, 0)}</TableCell>
                    <TableCell>{eur(o.total)}</TableCell>
                    <TableCell>{o.payment?.method === "stripe" ? `Stripe${o.payment?.last4 ? " ••••" + o.payment.last4 : ""}` : `Postepay ••••${o.payment?.last4 || ""}`}</TableCell>
                    <TableCell>
                      <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                        <SelectTrigger className="h-9 w-[170px] text-xs" data-testid={`status-select-${o.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ORDER_STATUSES.map((s) => (
                            <SelectItem key={s} value={s} data-testid={`status-option-${s}`} className="capitalize">{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {o.tracking_code && (
                        <p className="text-[11px] text-muted-foreground mt-1" data-testid={`tracking-${o.id}`}>
                          {o.tracking_url
                            ? <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Tracking: {o.tracking_code} ↗</a>
                            : <>Tracking: {o.tracking_code}</>}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => delOrder(o.id)} className="p-2 hover:text-destructive transition-colors" title="Elimina ordine" data-testid={`delete-order-${o.id}`}><Trash2 size={16} /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="newsletter" className="mt-8">
          <div className="flex justify-end mb-4">
            <a
              href={`mailto:?bcc=${subscribers.map((s) => s.email).join(",")}`}
              className="text-sm text-gold hover:underline"
              data-testid="newsletter-email-all"
            >
              Scrivi a tutti gli iscritti ↗
            </a>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <Table data-testid="subscribers-table">
              <TableHeader><TableRow><TableHead>Email</TableHead><TableHead className="text-right">Iscritto il</TableHead></TableRow></TableHeader>
              <TableBody>
                {subscribers.length === 0 ? (
                  <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-8">Nessun iscritto ancora.</TableCell></TableRow>
                ) : subscribers.map((s) => (
                  <TableRow key={s.id} data-testid={`subscriber-row-${s.id}`}>
                    <TableCell className="font-medium">{s.email}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{new Date(s.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!trackOrder} onOpenChange={(v) => { if (!v) setTrackOrder(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Segna come spedito</DialogTitle>
            <DialogDescription>Inserisci il codice di spedizione del corriere. Verrà inviato al cliente via email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Corriere</Label>
              <Select value={trackCarrier} onValueChange={setTrackCarrier}>
                <SelectTrigger data-testid="carrier-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CARRIERS.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`carrier-option-${c.id}`}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Codice di spedizione (tracking)</Label>
              <Input value={trackInput} onChange={(e) => setTrackInput(e.target.value)} placeholder="es. RR123456789IT" data-testid="tracking-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setTrackOrder(null)} data-testid="tracking-cancel">Annulla</Button>
            <Button className="rounded-full bg-gold hover:bg-gold/90 text-white" onClick={submitTracking} data-testid="tracking-save">Conferma spedizione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
